import type {
  CalendarEventLocationType,
  CalendarEventType,
} from '../../_types/calendar-event-type'

type BookingViewMode = 'full' | 'minimal'

interface PrefillValues {
  email: string
  name: string
}

/**
 * Reads the URL's prefill query parameters and returns the resolved values for
 * forwarding to the iframed form. Used when an external surface (e.g. a chat
 * bot or email link) already knows the guest's contact info and wants to save
 * the user a re-entry round trip.
 *
 * Supported query params:
 * - `prefillEmail` — pre-fills the form's first email-typed field
 * - `prefillName` — pre-fills the form's first text-typed field whose label
 *   contains "name"
 *
 * @param {URLSearchParams} searchParams - The page's query parameters.
 * @returns {PrefillValues} Trimmed prefill values (empty string when absent).
 */
const parsePrefillValues = (searchParams: URLSearchParams): PrefillValues => ({
  email: (searchParams.get('prefillEmail') ?? '').trim(),
  name: (searchParams.get('prefillName') ?? '').trim(),
})

/**
 * Resolves the forms-app base URL from build-time env config. Falls back to
 * the production URL when nothing is set so the booking page never points at
 * an undefined origin.
 *
 * @returns {string} Forms app base URL with no trailing slash.
 */
const resolveFormsBaseUrl = (): string => {
  const fromEnvironment = import.meta.env.VITE_FORMS_BASE_URL
  if (typeof fromEnvironment === 'string' && fromEnvironment.length > 0) {
    return fromEnvironment.replace(/\/$/u, '')
  }
  return 'https://forms.quickcasa.ai'
}

/**
 * Resolves the booking page's layout variant from the URL's `view` query
 * parameter. Anything other than `minimal` falls back to `full` so unknown
 * values land on the safe default.
 *
 * - `full` (default): two-column layout — image + title + rich-text description
 *   on the left, picker / form / confirmation on the right. Suits the standalone
 *   page at `book.quickcasa.ai/{agentUserId}/{slug}`.
 * - `minimal`: single column with just the picker / form / confirmation, no
 *   card chrome. Intended for iframe embeds where the host page already shows
 *   the agent's branding + event description.
 *
 * @param {URLSearchParams} searchParams - The page's query parameters.
 * @returns {BookingViewMode} The active layout variant.
 */
const parseViewMode = (searchParams: URLSearchParams): BookingViewMode => {
  const raw = searchParams.get('view')
  return raw === 'minimal' ? 'minimal' : 'full'
}

const LOCATION_LABELS: Record<CalendarEventLocationType, string> = {
  'property-tour': 'On-site property tour',
  phone: 'Phone call',
  video: 'Video call',
  'in-person': 'In person',
  custom: '',
}

/**
 * Returns a human-friendly location label for the booking confirmation card.
 * Falls back to the event type's `customLocation` field when the type is
 * `custom`, since custom locations don't have a fixed label.
 *
 * @param {CalendarEventType} eventType - The loaded event type.
 * @returns {string} Display label for the booking location.
 */
const getLocationLabel = (eventType: CalendarEventType): string => {
  if (eventType.locationType === 'custom') {
    return eventType.customLocation
  }
  return LOCATION_LABELS[eventType.locationType]
}

export {
  getLocationLabel,
  parseViewMode,
  parsePrefillValues,
  resolveFormsBaseUrl,
}
export type { BookingViewMode, PrefillValues }
